import { css, SerializedStyles } from '@emotion/react';

import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { ACCENT, DARK_BG } from '../../scanVideoMediaStyles';

type PlaybackTimelineDensity = 'minimal' | 'segment' | 'filmstrip';

/** Gap between group bands on the scrubber rail (mock `pov-seg` inset). */
export const GROUP_TIMELINE_SEGMENT_GAP_PX = 5;

/**
 * The panel + group-timeline presentation, bundled into one object the same way
 * `MetaList` and `TaskStatusBadge` keep theirs — so when design blesses the final look a re-skin
 * spreads `DEFAULT_VIDEO_PLAYBACK_PANEL_STYLES` and overrides only what differs,
 * and the swap can hang off a feature flag through the `styles` prop.
 */
export type VideoPlaybackPanelStyles = {
  frame: SerializedStyles;
  video: SerializedStyles;
  overlayBar: SerializedStyles;
  controls: SerializedStyles;
  controlButton: SerializedStyles;
  captureButton: SerializedStyles;
  controlTime: SerializedStyles;
  timeline: SerializedStyles;
  timelineTrack: (density: PlaybackTimelineDensity) => SerializedStyles;
  timelineTrackGrouped: SerializedStyles;
  timelineTrackHasHighlightGroup: SerializedStyles;
  timelineMinimalPlayedFill: (widthFraction: number) => SerializedStyles;
  timelinePlayheadKnob: (leftFraction: number) => SerializedStyles;
  timelineGroupSegment: (
    leftFraction: number,
    widthFraction: number,
    isHighlightedGroup: boolean,
    dimNonHighlightedGroups: boolean,
    isHoveredGroup: boolean,
  ) => SerializedStyles;
  timelineGroupSegmentFill: (playedFraction: number) => SerializedStyles;
  timelineGroupHoverLabel: SerializedStyles;
  timelineGroupHitTarget: (leftFraction: number, widthFraction: number) => SerializedStyles;
  timelineAnchorTick: (leftFraction: number) => SerializedStyles;
  timelineFilmstripThumb: (leftFraction: number) => SerializedStyles;
};

export const DEFAULT_VIDEO_PLAYBACK_PANEL_STYLES: VideoPlaybackPanelStyles = {
  frame: css`
    label: VIDEO_PLAYBACK_FRAME;
    margin-top: 18px;
    position: relative;
    background: ${DARK_BG};
    border: 1px solid rgba(255, 255, 255, 0.12);
  `,
  video: css`
    label: VIDEO_PLAYBACK_VIDEO;
    display: block;
    width: 100%;
    max-height: min(70vh, 560px);
    aspect-ratio: 16 / 9;
    object-fit: contain;
    cursor: pointer;
  `,
  overlayBar: css`
    label: VIDEO_PLAYBACK_OVERLAY_BAR;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 22px 14px 10px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0));
    z-index: 3;
    pointer-events: none;

    & > * {
      pointer-events: auto;
    }
  `,
  controls: css`
    label: VIDEO_PLAYBACK_CONTROLS;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
    padding: 0;
  `,
  controlButton: css`
    label: VIDEO_PLAYBACK_CONTROL_BUTTON;
    width: 26px;
    height: 26px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: 0;
    color: ${styleguideColors.white};
    cursor: pointer;
    opacity: 0.88;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }

    svg {
      width: 17px;
      height: 17px;
    }
  `,
  captureButton: css`
    label: VIDEO_PLAYBACK_CAPTURE_BUTTON;
    margin-left: auto;
  `,
  controlTime: css`
    label: VIDEO_PLAYBACK_CONTROL_TIME;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(11)};
    font-weight: 700;
    letter-spacing: 0.04em;
    color: ${styleguideColors.white};
    font-variant-numeric: tabular-nums;
  `,
  timeline: css`
    label: VIDEO_PLAYBACK_TIMELINE;
    position: relative;
    user-select: none;
  `,
  timelineTrack: (density: PlaybackTimelineDensity) =>
    css`
    label: VIDEO_PLAYBACK_TIMELINE_TRACK;
    position: relative;
    height: ${density === 'filmstrip' ? 56 : 4}px;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    touch-action: none;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: -8px;
      bottom: -8px;
    }
  `,
  timelineTrackGrouped: css`
    label: VIDEO_PLAYBACK_TIMELINE_TRACK_GROUPED;
    background: rgba(255, 255, 255, 0.14);
  `,
  timelineTrackHasHighlightGroup: css``,
  timelineMinimalPlayedFill: (widthFraction: number) =>
    css`
    label: VIDEO_PLAYBACK_MINIMAL_PLAYED_FILL;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${widthFraction * 100}%;
    background: ${ACCENT};
    z-index: 1;
    pointer-events: none;
  `,
  timelinePlayheadKnob: (leftFraction: number) =>
    css`
    label: VIDEO_PLAYBACK_PLAYHEAD_KNOB;
    position: absolute;
    top: 50%;
    left: ${leftFraction * 100}%;
    width: 11px;
    height: 11px;
    margin: -5.5px 0 0 -5.5px;
    background: ${ACCENT};
    pointer-events: none;
    z-index: 5;
  `,
  timelineGroupSegment: (
    leftFraction: number,
    widthFraction: number,
    isHighlightedGroup: boolean,
    dimNonHighlightedGroups: boolean,
    isHoveredGroup: boolean,
  ) =>
    css`
    label: VIDEO_PLAYBACK_GROUP_SEGMENT;
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${leftFraction * 100}%;
    width: calc(${Math.max(widthFraction, 0) * 100}% - ${GROUP_TIMELINE_SEGMENT_GAP_PX}px);
    background: rgba(255, 255, 255, ${isHighlightedGroup ? 0.62 : 0.34});
    cursor: pointer;
    z-index: 2;
    pointer-events: none;
    transform: ${isHoveredGroup ? 'scaleY(2)' : 'none'};
    ${dimNonHighlightedGroups && !isHighlightedGroup ? 'opacity: 0.42;' : ''}
  `,
  timelineGroupSegmentFill: (playedFraction: number) =>
    css`
    label: VIDEO_PLAYBACK_GROUP_SEGMENT_FILL;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${playedFraction * 100}%;
    background: ${ACCENT};
    pointer-events: none;
  `,
  timelineGroupHoverLabel: css`
    label: VIDEO_PLAYBACK_GROUP_HOVER_LABEL;
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    max-width: min(320px, 90%);
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(10)};
    font-weight: 700;
    letter-spacing: 0.05em;
    color: #fff;
    background: rgba(0, 0, 0, 0.86);
    padding: 5px 9px;
    pointer-events: none;
    z-index: 6;
  `,
  timelineGroupHitTarget: (leftFraction: number, widthFraction: number) =>
    css`
    label: VIDEO_PLAYBACK_GROUP_HIT;
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${leftFraction * 100}%;
    width: calc(${Math.max(widthFraction, 0) * 100}% - ${GROUP_TIMELINE_SEGMENT_GAP_PX}px);
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    z-index: 4;
  `,
  timelineAnchorTick: (leftFraction: number) =>
    css`
    label: VIDEO_PLAYBACK_ANCHOR_TICK;
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${leftFraction * 100}%;
    width: 2px;
    margin-left: -1px;
    background: rgba(255, 255, 255, 0.85);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35);
    pointer-events: none;
    z-index: 2;
  `,
  timelineFilmstripThumb: (leftFraction: number) =>
    css`
    label: VIDEO_PLAYBACK_FILMSTRIP_THUMB;
    position: absolute;
    top: 3px;
    bottom: 3px;
    left: ${leftFraction * 100}%;
    width: 56px;
    transform: translateX(-50%);
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.92);
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.35);
    pointer-events: none;
    z-index: 3;
  `,
};
